import React, { useState } from "react";
import { SITE_CONTENT } from "../content";

const { menu } = SITE_CONTENT.sections;

// Badge color lookup
const BADGE_COLORS = {
  Bestseller: { bg: "#e53935", color: "#fff" },
  Special: { bg: "#e53935", color: "#fff" },
  "Value Pick": { bg: "#e53935", color: "#fff" },
  Spicy: { bg: "#e53935", color: "#fff" },
  Egg: { bg: "#43a047", color: "#fff" },
  Veg: { bg: "#43a047", color: "#fff" },
};

const TodaysMenuSection = () => {
  const [activeTab, setActiveTab] = useState(menu.tabs[0].id);
  const [selectedItem, setSelectedItem] = useState(null);
  const items = menu.panels[activeTab] || [];

  return (
    <section id="menu" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* ── Title ── */}
        <div className="text-center mb-10">
          <h2 className="text-5xl md:text-6xl font-['Dancing_Script'] text-[#cd3a27] font-medium leading-none">
            Today's Menu
          </h2>
          {/* Ornamental Divider */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="h-[1.5px] w-16 bg-gradient-to-r from-transparent via-[#cd3a27] to-[#cd3a27]" />
            <div className="w-2.5 h-2.5 border border-[#cd3a27] rotate-45 flex items-center justify-center">
              <div className="w-1 h-1 bg-[#cd3a27]" />
            </div>
            <div className="h-[1.5px] w-16 bg-gradient-to-l from-transparent via-[#cd3a27] to-[#cd3a27]" />
          </div>
          {/* Subtitle from content */}
          <p className="mt-4 text-sm text-gray-500 max-w-lg mx-auto leading-relaxed">
            {menu.subtitle}
          </p>
        </div>

        {/* ── Tab buttons ── */}
        <div className="flex flex-wrap justify-center items-center gap-2 md:gap-3 mb-14 max-w-5xl mx-auto">
          {menu.tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2 text-[12px] md:text-[13px] font-bold tracking-widest transition-all duration-300 uppercase rounded-xl border-2 ${
                activeTab === tab.id
                  ? "bg-[#cd3a27] text-white border-[#cd3a27] shadow-lg shadow-[#cd3a27]/30 scale-105"
                  : "bg-white text-gray-500 border-gray-100 hover:border-[#cd3a27]/40 hover:text-[#cd3a27] hover:bg-gray-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Menu Grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {items.map((item, idx) => (
            <div
              key={idx}
              onClick={() => setSelectedItem(item)}
              className="group bg-white border border-gray-100 rounded-3xl p-4 md:p-6 flex flex-col sm:flex-row items-center sm:items-start gap-6 hover:shadow-2xl hover:border-[#cd3a27]/30 transition-all duration-500 cursor-pointer relative overflow-hidden"
            >
              {/* Subtle background glow on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#cd3a27]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

              {/* Image Container */}
              <div className="relative w-[140px] h-[140px] flex-shrink-0 rounded-2xl overflow-hidden shadow-sm bg-black flex items-center justify-center transform group-hover:-translate-y-1 group-hover:shadow-lg transition-all duration-500">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                ) : (
                  <span className="text-6xl group-hover:scale-110 transition-transform duration-700">
                    {item.emoji}
                  </span>
                )}

                {/* Dietary Badge */}
                {item.badge && (
                  <div className="absolute top-2 left-2 z-10">
                    <span
                      className="px-2 py-1 text-[10px] font-black uppercase tracking-wider rounded-lg shadow-sm backdrop-blur-md"
                      style={{
                        background: (BADGE_COLORS[item.badge] || { bg: "#555" })
                          .bg,
                        color: "#fff",
                      }}
                    >
                      {item.badge}
                    </span>
                  </div>
                )}
              </div>

              {/* Content Container */}
              <div className="flex-1 flex flex-col h-full z-10 w-full text-center sm:text-left">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2">
                  <h3 className="font-bold text-gray-900 text-[18px] md:text-[20px] leading-snug group-hover:text-[#cd3a27] transition-colors duration-300">
                    {item.name}
                  </h3>
                  {item.price && (
                    <span className="text-[#cd3a27] font-black text-xl whitespace-nowrap bg-[#cd3a27]/10 px-3 py-1 rounded-xl">
                      ₹{item.price}
                    </span>
                  )}
                </div>

                <p className="text-[13px] text-gray-500 leading-relaxed mb-4 line-clamp-2">
                  {item.desc}
                </p>

                <div className="mt-auto flex flex-wrap items-center justify-center sm:justify-start gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest text-gray-600 bg-gray-100 group-hover:bg-white group-hover:border-[#cd3a27]/20 border border-transparent transition-all">
                    {item.tag}
                  </span>

                  {/* View Details Hint */}
                  <span className="ml-auto text-[11px] font-bold text-[#cd3a27] opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300 hidden sm:inline-block">
                    VIEW DETAILS →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Disclaimer ── */}
        <p className="text-center mt-10 text-[12px] text-gray-400">
          {menu.note}
        </p>
      </div>

      {/* ── Item Modal ── */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 transition-opacity duration-300"
          onClick={() => setSelectedItem(null)}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row transform transition-all"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 p-2 bg-white/50 backdrop-blur-md hover:bg-gray-200 text-gray-900 rounded-full shadow-sm transition-colors z-10"
              onClick={() => setSelectedItem(null)}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>

            {/* Image Section */}
            <div className="md:w-1/2 bg-black flex items-center justify-center p-8">
              {selectedItem.image ? (
                <img
                  src={selectedItem.image}
                  alt={selectedItem.name}
                  className="max-w-full max-h-[50vh] md:max-h-[70vh] object-contain hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <span className="text-9xl">{selectedItem.emoji}</span>
              )}
            </div>

            {/* Details Section */}
            <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center bg-white relative">
              <div className="mb-4">
                {selectedItem.badge && (
                  <span
                    className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3"
                    style={{
                      background: (
                        BADGE_COLORS[selectedItem.badge] || { bg: "#555" }
                      ).bg,
                      color: "#fff",
                    }}
                  >
                    {selectedItem.badge}
                  </span>
                )}
                <h3 className="text-3xl font-bold text-gray-900 mb-2 leading-tight">
                  {selectedItem.name}
                </h3>
                {selectedItem.price && (
                  <p className="text-2xl font-bold text-[#cd3a27] mb-6">
                    ₹{selectedItem.price}
                  </p>
                )}
              </div>

              <div className="h-px w-full bg-gray-100 mb-6"></div>

              <p className="text-gray-600 leading-relaxed mb-8 text-lg">
                {selectedItem.desc}
              </p>

              <div className="mt-auto">
                <span className="inline-block text-xs font-semibold uppercase tracking-widest text-gray-400 border-2 border-gray-100 px-4 py-2 rounded-full">
                  {selectedItem.tag}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TodaysMenuSection;
